/**
 * @file <bk-pickup-availability>: local pickup of the selected variant (blocks/_product-pickup.liquid).
 *
 * The element starts empty and asks Shopify for the pickup section of the current variant
 * (`<root>variants/<id>/?section_id=product-pickup-availability`, where the section reads
 * `product_variant.store_availabilities`). Nothing is shown when the store has no pickup location
 * for that variant, so a store without local pickup never shows an empty row. It refreshes on
 * `bk:variant:change` for its own section and empties itself when no variant is selected.
 */
import { subscribe } from '@theme/utils';

class BkPickupAvailability extends HTMLElement {
  /** @type {AbortController | null} */
  #request = null;

  /** @type {(() => void) | null} */
  #unsubscribe = null;

  connectedCallback() {
    this.#unsubscribe = subscribe('bk:variant:change', (detail) => this.#handleVariantChange(detail));
    if (this.dataset.variantId && this.dataset.available === 'true') this.#load(this.dataset.variantId);
  }

  disconnectedCallback() {
    this.#request?.abort();
    this.#request = null;
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  /** @param {{ variant?: { id?: number, available?: boolean } | null, sectionId?: string } | undefined} detail */
  #handleVariantChange(detail) {
    if (!detail || (detail.sectionId && detail.sectionId !== this.dataset.sectionId)) return;
    const variant = detail.variant;
    if (!variant?.id || variant.available === false) {
      this.#request?.abort();
      this.replaceChildren();
      return;
    }
    this.dataset.variantId = String(variant.id);
    this.#load(String(variant.id));
  }

  /** @param {string} variantId */
  async #load(variantId) {
    const section = this.dataset.pickupSection;
    if (!section) return;

    this.#request?.abort();
    this.#request = new AbortController();

    const root = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
    const url = new URL(`${root}variants/${encodeURIComponent(variantId)}/`, window.location.origin);
    url.searchParams.set('section_id', section);

    try {
      const response = await fetch(url, { signal: this.#request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-pickup-content]');
      if (content) this.replaceChildren(document.importNode(content, true));
      else this.replaceChildren();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.replaceChildren();
    }
  }
}

if (!customElements.get('bk-pickup-availability')) customElements.define('bk-pickup-availability', BkPickupAvailability);
